import { useState } from "react";
import { useAuth } from "../../context/contextAuth";
import { getIpResults } from "../../api/get/get-network";
import { scanIp, scanNIp } from "../../api/post/post-network";

const useNetworkData = () => {
  const { userId, user } = useAuth();

  // MAIN STATES
  const [networkLoading, setNetworkLoading] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentUuid, setAgentUuid] = useState("");
  const [error, setError] = useState(null);
  const [scanType, setScanType] = useState("");
  const [selectedOS, setSelectedOS] = useState("");

  // RESULTS FROM BACKEND
  const [networkResults, setNetworkResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScanIndex, setSelectedScanIndex] = useState(0);

  // TABLES FOR UI
  const [ipVulTable, setIpVulTable] = useState([]);
  const [ipEpssTable, setIpEpssTable] = useState([]);
  const [ipServices, setIpServices] = useState([]);

  // SCORE DATA
  const [TVulScore, setTVulScore] = useState(0);
  const [CVulScore, setCVulScore] = useState(0);
  const [saveScore, setSaveScore] = useState(0);

  // DASHBOARD CONTROL FLAGS
  const [ipScanLoading, setIpScanLoading] = useState(false);
  const [ipDashboardView, setIpDashboardView] = useState(false);
  const [ipHeader, setIpHeader] = useState(false);

  const initData = async () => {
    if (!user) {
      resetState();
      return;
    }

    await fetchNetworkResults(userId);
    if(!networkLoading) await loadNetworkData(0);
  };
  
  const fetchNetworkResults = async (userId) => {
    try {
      const results = await getIpResults(userId);
      const orderedResults = Array.isArray(results)
        ? [...results].sort((a, b) => {
            const aTime = new Date(a?.created_at || 0).getTime();
            const bTime = new Date(b?.created_at || 0).getTime();
            return aTime - bTime;
          })
        : [];

      setNetworkResults(orderedResults);
      if (orderedResults.length > 0) {
        const latestIndex = orderedResults.length - 1;
        setCurrentIndex(latestIndex);
        setSelectedScanIndex(latestIndex);
        setIpDashboardView(true);
      }
      return orderedResults;
    } catch (error) {
      setNetworkResults([]);
      return [];
    }
  };

  const loadNetworkData = async (index) => {
    const scan = networkResults[index];
    if (!scan) return;

    setCurrentIndex(index);
    setIpVulTable(scan.vuln_data || []);
    setIpEpssTable(scan.epss_data || []);
    setIpServices(scan.services_data || []);
    setTVulScore(scan.t_vul);
    setCVulScore(scan.c_vul);
    setSaveScore(scan.ss);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIpScanLoading(true);
    setIpDashboardView(true);
    setIpHeader(false);

    try {
      await scanIp(userId, agentUuid, scanType);
      const results = await fetchNetworkResults(userId);
      await loadNetworkData(results.length - 1);
    } catch (err) {
      setIpDashboardView(false);
      setError({
        message: err.message,
        card: err.card || false
      });
    } finally {
      setIpScanLoading(false);
      setIpHeader(true);
      setAgentUuid("");
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const handleNSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIpScanLoading(true);
    setIpDashboardView(true);
    setIpHeader(false);

    try {
      await scanNIp(userId, agentUuid, scanType);
      const results = await fetchNetworkResults(userId);
      await loadNetworkData(results.length - 1);
    } catch (error) {
      setIpDashboardView(false);
      setError(error.message);
    } finally {
      setIpScanLoading(false);
      setIpHeader(true);
      setAgentUuid("");
      setTimeout(() => setError(null), 6000);
    }
  };

  const resetState = () => {
    setNetworkLoading(false);
    setAgentUuid("");
    setError(null);
    setScanType('');

    setNetworkResults([]);
    setCurrentIndex(0);

    setIpVulTable([]);
    setIpEpssTable([]);
    setIpServices([]);

    setTVulScore(0);
    setCVulScore(0);
    setSaveScore(0);

    setIpScanLoading(false);
    setIpDashboardView(false);
    setIpHeader(false);
  };

  return {
    // STATE
    networkLoading,
    agentUuid, setAgentUuid,
    agentLoading, setAgentLoading,
    error, setError,
    scanType, setScanType,
    selectedOS, setSelectedOS,

    networkResults,
    currentIndex, setCurrentIndex,
    selectedScanIndex, setSelectedScanIndex,

    ipVulTable, setIpVulTable,
    ipEpssTable,
    ipServices,

    TVulScore,
    CVulScore,
    saveScore,

    ipScanLoading,
    ipDashboardView, setIpDashboardView,
    ipHeader, setIpHeader,

    initData,
    loadNetworkData,
    handleSubmit,
    handleNSubmit,
    resetState,
  };
};

export default useNetworkData;
